import { ScrollArea } from '@ibsys/kit';
import { FC } from 'react';
import { cn } from '@/shared/lib';
import { buildEvaluationTargetKey, EVALUATION_TARGET_HIGHLIGHT_CLASS, useEvaluationTarget } from '../model';
import { DownloadExpertReportButton } from './DownloadExpertReportButton';
import { EvaluationBlockRatingEditor } from './EvaluationBlockRatingEditor';
import { EvaluationRulesList } from './EvaluationRulesList';
import type { FlatRow } from './ExpertReportTab';

type Props = {
  rows: FlatRow[];
  expertReportId: number;
  conversationId: number;
};

const DEPTH_PADDING: Record<number, string> = { 0: 'pl-3', 1: 'pl-7', 2: 'pl-11', 3: 'pl-15' };
const getDepthPadding = (depth: number) => DEPTH_PADDING[depth] ?? 'pl-15';

/**
 * Отступы списка правил: слева меньше отступа наименования той же глубины на поле, которое правило
 * держит само под рамку выделения, — так текст правила встаёт на общую линию. Правое поле под ту же
 * рамку: иначе она упиралась бы в край заключения.
 */
const RULES_DEPTH_PADDING: Record<number, string> = { 0: 'pl-2', 1: 'pl-6', 2: 'pl-10', 3: 'pl-14' };
const getRulesDepthPadding = (depth: number) => `${RULES_DEPTH_PADDING[depth] ?? 'pl-14'} pr-1`;

/** Отступ балла от правого края заключения — парный к отступу наименования верхнего уровня слева. */
const RATING_RIGHT_PADDING = 'pr-3';

export const ExpertReportEvaluationTable: FC<Props> = ({ rows, expertReportId, conversationId }) => {
  const evaluationTarget = useEvaluationTarget();

  return (
    <ScrollArea className="h-full" viewPortProps={{ ref: evaluationTarget.viewportRef }}>
      <div className="flex flex-col text-sm">
        {rows.map((row) => {
          const isTargetBlock =
            !row.isTotal && evaluationTarget.targetKey === buildEvaluationTargetKey('block', row.blockId);

          return (
            <div
              key={row.id}
              ref={isTargetBlock ? evaluationTarget.attachTarget : undefined}
              className={cn(
                'border-b py-2',
                row.isTotal && 'font-bold',
                row.isParent && 'bg-secondary font-bold',
                isTargetBlock && EVALUATION_TARGET_HIGHLIGHT_CLASS,
              )}
            >
              <div
                className={cn(
                  'flex items-center gap-2',
                  getDepthPadding(row.isTotal ? 0 : row.depth),
                  RATING_RIGHT_PADDING,
                )}
              >
                <span className="flex-1">{row.name}</span>
                {row.isTotal && (
                  <DownloadExpertReportButton conversationId={conversationId} expertReportId={expertReportId} />
                )}
                {row.isTotal || row.isParent ? (
                  <span className="text-muted-foreground shrink-0">
                    {row.rating} / {row.maxRating}
                  </span>
                ) : (
                  <EvaluationBlockRatingEditor
                    conversationId={conversationId}
                    expertReportId={expertReportId}
                    blockId={row.blockId}
                    rating={row.rating}
                    maxRating={row.maxRating}
                    manualRating={row.manualRating}
                  />
                )}
              </div>
              {row.rules && row.rules.length > 0 && (
                <div className={cn('mt-1 text-muted-foreground', getRulesDepthPadding(Math.min(row.depth + 1, 3)))}>
                  <EvaluationRulesList
                    rules={row.rules}
                    conversationId={conversationId}
                    expertReportId={expertReportId}
                    blockId={row.blockId}
                    evaluationTarget={evaluationTarget}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};
